import { useCallback, useEffect, useState } from 'react';
import { Area, MediaSize, Point } from 'react-easy-crop/types';
import Cropper from 'react-easy-crop';
import classNames from 'classnames';
import { BiHorizontalCenter } from '@react-icons/all-files/bi/BiHorizontalCenter';
import { BiVerticalCenter } from '@react-icons/all-files/bi/BiVerticalCenter';
import { BiCheck } from '@react-icons/all-files/bi/BiCheck';

import { FiCheck } from '@react-icons/all-files/fi/FiCheck';
import { TiPlus } from '@react-icons/all-files/ti/TiPlus';
import { TiMinus } from '@react-icons/all-files/ti/TiMinus';

import { CgChevronLeft } from '@react-icons/all-files/cg/CgChevronLeft';
import { cropImage } from './imageUtils';
import { Button } from '../Button';

function AspectRatioButton(props: {
  setAspectRatio: (aspectRatio: number) => void;
  aspectRatio: number;
  label: string;
}) {
  const WIDTH = 1.5;
  return (
    <button
      type="button"
      className="sm:w-full p-3 h-10 sm:h-auto bg-gray-200 border border-gray-300 rounded-md flex items-center text-xs justify-center"
      onClick={() => props.setAspectRatio(props.aspectRatio)}
    >
      <div
        className="border-2 border-gray-600 border-dashed mr-2"
        style={{
          width: `${WIDTH}rem`,
          height: `${WIDTH / props.aspectRatio}rem`,
        }}
      />
      {props.label}
    </button>
  );
}

function AspectRatioSelector(props: {
  setAspectRatio: (aspectRatio: number) => void;
}) {
  return (
    <div className="flex w-full justify-center mb-4 space-x-2 sm:space-x-5">
      <AspectRatioButton
        setAspectRatio={props.setAspectRatio}
        aspectRatio={1}
        label="Square"
      />
      <AspectRatioButton
        setAspectRatio={props.setAspectRatio}
        aspectRatio={3 / 2}
        label="Rectangle"
      />
      <AspectRatioButton
        setAspectRatio={props.setAspectRatio}
        aspectRatio={2}
        label="Wide"
      />
    </div>
  );
}

export function ImageCropper(props: {
  uploadedImage: File;
  setIsOpen: (isOpen: boolean) => void;
  cropperShape: 'rect' | 'round';
  handleSubmit: (data: Blob) => void;
  isLoading?: boolean;
  adjustableAspectRatio?: boolean;
  centeringControls?: boolean;
}) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  });
  const [displayImageUrl, setDisplayImageUrl] = useState<string>();
  const [croppedImage, setCroppedImage] = useState<Blob>();
  const [croppedImageUrl, setCroppedImageUrl] = useState<string>();
  const [showImage, setShowImage] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [mediaSize, setMediaSize] = useState<MediaSize>();

  useEffect(() => {
    setDisplayImageUrl(URL.createObjectURL(props.uploadedImage));
  }, []);

  const onCropComplete = useCallback(
    (_: Area | undefined, areaPixels: Area) => {
      setCroppedAreaPixels(areaPixels);
    },
    [],
  );

  async function getCroppedImage(areaPixels: Area) {
    try {
      const image = await cropImage(
        props.uploadedImage,
        areaPixels,
        aspectRatio,
      );

      setCroppedImage(image);
      setCroppedImageUrl(URL.createObjectURL(image));
      setShowImage(!showImage);
    } catch (e: any) {
      console.error(e.message);
    }
  }

  return (
    <>
      {props.adjustableAspectRatio && !showImage && (
        <AspectRatioSelector setAspectRatio={setAspectRatio} />
      )}
      <div className="relative h-96 max-w-md mx-auto mb-5 rounded-md overflow-hidden">
        {!showImage ? (
          <>
            {props.centeringControls && (
              <div className="absolute top-3 left-3 z-10 flex space-x-1">
                {!!crop.x && (
                  <button
                    type="button"
                    className="p-1 rounded-md flex items-center justify-center bg-gray-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCrop({ ...crop, x: 0 });

                      onCropComplete(undefined, {
                        ...croppedAreaPixels,
                        x: Math.round(
                          (mediaSize!.naturalWidth - croppedAreaPixels.width) /
                            2,
                        ),
                      });
                    }}
                  >
                    <BiHorizontalCenter />
                  </button>
                )}
                {!!crop.y && (
                  <button
                    type="button"
                    className="p-1 rounded-md flex items-center justify-center bg-gray-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCrop({ ...crop, y: 0 });
                      onCropComplete(undefined, {
                        ...croppedAreaPixels,
                        y: Math.round(
                          (mediaSize!.naturalHeight -
                            croppedAreaPixels.height) /
                            2,
                        ),
                      });
                    }}
                  >
                    <BiVerticalCenter />
                  </button>
                )}
              </div>
            )}
            <Cropper
              image={displayImageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropShape={props.cropperShape}
              restrictPosition={zoom >= 1}
              onMediaLoaded={setMediaSize}
              minZoom={0.75}
            />
          </>
        ) : (
          <div className="flex h-full w-full justify-center items-center">
            <img
              src={croppedImageUrl}
              alt="Cropped"
              className={classNames(
                props.cropperShape === 'round' && 'rounded-full',
              )}
            />
          </div>
        )}
      </div>
      {!showImage && (
        <div className="w-full mb-5 px-5 py-6 bg-gray-100 rounded-md">
          <div className="w-full flex flex-row justify-center space-x-5">
            <TiMinus />
            <input
              type="range"
              value={zoom}
              min={0.75}
              max={3}
              step={0.05}
              onChange={(event) => {
                setZoom(parseFloat(event.target.value));
              }}
              className="w-2/3"
            />
            <TiPlus />
          </div>
        </div>
      )}
      <div className="flex justify-center space-x-5">
        <Button
          disabled={props.isLoading}
          onClick={async () => getCroppedImage(croppedAreaPixels)}
          fullWidth={false}
          variant={!showImage ? undefined : 'outline'}
          leftIcon={!showImage ? <FiCheck /> : <CgChevronLeft />}
        >
          {!showImage ? 'Finish cropping' : 'Back'}
        </Button>
        {showImage && (
          <Button
            loading={props.isLoading}
            onClick={async () => {
              if (croppedImage) {
                await props.handleSubmit(croppedImage);
              }
            }}
            leftIcon={<BiCheck />}
            variant="success"
            fullWidth={false}
            analyticsProperties={{
              name: 'Profile Picture Updated',
              properties: {
                resized_image: !(zoom === 1),
                repositioned_image: !(crop.x === 0 && crop.y === 0),
              },
            }}
          >
            Save
          </Button>
        )}
      </div>
    </>
  );
}
